import { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplayIcon from '@mui/icons-material/Replay';
import { useAuthStore } from '../../stores/root_store';
import { rqJobsApi } from '../../api/rq_jobs_api';
import type { RqJob } from '../../api/rq_jobs_api';

type TabValue = 'scheduled' | 'queued' | 'failed';

const TAB_ROUTES: Record<TabValue, string> = {
  scheduled: '/admin/jobs/scheduled',
  queued: '/admin/jobs/queued',
  failed: '/admin/jobs/failed',
};

const ROUTE_TO_TAB: Record<string, TabValue> = {
  scheduled: 'scheduled',
  queued: 'queued',
  failed: 'failed',
};

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
}

function formatFuncName(funcName: string): string {
  const parts = funcName.split('.');
  return parts[parts.length - 1];
}

function formatArgs(args: unknown[], kwargs: Record<string, unknown>): string {
  const parts: string[] = [];
  if (args.length > 0) {
    parts.push(JSON.stringify(args));
  }
  if (Object.keys(kwargs).length > 0) {
    parts.push(JSON.stringify(kwargs));
  }
  const result = parts.join(', ');
  return result.length > 100 ? result.substring(0, 100) + '...' : result;
}

function getStatusColor(status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
  switch (status) {
    case 'scheduled':
      return 'info';
    case 'queued':
      return 'primary';
    case 'started':
      return 'warning';
    case 'finished':
      return 'success';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
}

interface JobDetailDialogProps {
  job: RqJob | null;
  open: boolean;
  onClose: () => void;
  loading: boolean;
  onDelete?: (jobId: string) => void;
  onRequeue?: (jobId: string) => void;
  actionLoading?: boolean;
}

function JobDetailDialog({ job, open, onClose, loading, onDelete, onRequeue, actionLoading }: JobDetailDialogProps) {
  const isFailed = job?.status === 'failed';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Job Details
        {job && (
          <Chip
            label={job.status}
            color={getStatusColor(job.status)}
            size="small"
            sx={{ ml: 2 }}
          />
        )}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : job ? (
          <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
            <Typography variant="subtitle2" color="text.secondary">Job ID</Typography>
            <Typography sx={{ mb: 2, wordBreak: 'break-all' }}>{job.job_id}</Typography>

            <Typography variant="subtitle2" color="text.secondary">Queue</Typography>
            <Typography sx={{ mb: 2 }}>{job.queue || 'default'}</Typography>

            <Typography variant="subtitle2" color="text.secondary">Function</Typography>
            <Typography sx={{ mb: 2, wordBreak: 'break-all' }}>{job.func_name}</Typography>

            {job.description && (
              <>
                <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                <Typography sx={{ mb: 2 }}>{job.description}</Typography>
              </>
            )}

            <Typography variant="subtitle2" color="text.secondary">Arguments</Typography>
            <Paper variant="outlined" sx={{ p: 1, mb: 2, maxHeight: 200, overflow: 'auto' }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {JSON.stringify(job.args, null, 2)}
              </pre>
            </Paper>

            <Typography variant="subtitle2" color="text.secondary">Keyword Arguments</Typography>
            <Paper variant="outlined" sx={{ p: 1, mb: 2, maxHeight: 200, overflow: 'auto' }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {JSON.stringify(job.kwargs, null, 2)}
              </pre>
            </Paper>

            <Typography variant="subtitle2" color="text.secondary">Timestamps</Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">Created: {formatDate(job.created_at)}</Typography>
              <Typography variant="body2">Enqueued: {formatDate(job.enqueued_at)}</Typography>
              <Typography variant="body2">Started: {formatDate(job.started_at)}</Typography>
              <Typography variant="body2">Ended: {formatDate(job.ended_at)}</Typography>
              {job.scheduled_time && (
                <Typography variant="body2">Scheduled: {formatDate(job.scheduled_time)}</Typography>
              )}
            </Box>

            {job.exc_info && (
              <>
                <Typography variant="subtitle2" color="error">Traceback</Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1,
                    maxHeight: 300,
                    overflow: 'auto',
                    bgcolor: 'error.main',
                    color: 'error.contrastText',
                  }}
                >
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {job.exc_info}
                  </pre>
                </Paper>
              </>
            )}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        {isFailed && onRequeue && job && (
          <Button
            onClick={() => onRequeue(job.job_id)}
            startIcon={<ReplayIcon />}
            disabled={actionLoading}
            color="primary"
          >
            Requeue
          </Button>
        )}
        {isFailed && onDelete && job && (
          <Button
            onClick={() => onDelete(job.job_id)}
            startIcon={<DeleteIcon />}
            disabled={actionLoading}
            color="error"
          >
            Delete
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export const AdminJobsPage = observer(() => {
  const navigate = useNavigate();

  const params = useParams<{ tab?: string }>();
  const authStore = useAuthStore();

  const [jobs, setJobs] = useState<RqJob[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedJob, setSelectedJob] = useState<RqJob | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Determine current tab from URL
  const tabFromUrl = params.tab || 'scheduled';
  const currentTab: TabValue = ROUTE_TO_TAB[tabFromUrl] || 'scheduled';

  const hasSudo = authStore.roles.includes('sudo');

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let response;
      switch (currentTab) {
        case 'scheduled':
          response = await rqJobsApi.getScheduledJobs();
          break;
        case 'queued':
          response = await rqJobsApi.getQueuedJobs();
          break;
        case 'failed':
          response = await rqJobsApi.getFailedJobs();
          break;
      }
      setJobs(response.jobs);
      setTotal(response.total);
    } catch (err) {
      console.error('RQ Jobs error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  }, [currentTab]);

  useEffect(() => {
    if (hasSudo) {
      fetchJobs();
    }
  }, [fetchJobs, hasSudo]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    navigate(TAB_ROUTES[newValue]);
  };

  const handleViewJob = async (jobId: string) => {
    setDialogOpen(true);
    setDialogLoading(true);
    setSelectedJob(null);

    try {
      const job = await rqJobsApi.getJobDetails(jobId);
      setSelectedJob(job);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch job details');
      setDialogOpen(false);
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedJob(null);
  };

  const handleDeleteJob = async (jobId: string) => {
    setActionLoading(true);
    try {
      await rqJobsApi.deleteJob(jobId);
      setDialogOpen(false);
      setSelectedJob(null);
      fetchJobs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete job');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequeueJob = async (jobId: string) => {
    setActionLoading(true);
    try {
      await rqJobsApi.requeueJob(jobId);
      setDialogOpen(false);
      setSelectedJob(null);
      fetchJobs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to requeue job');
    } finally {
      setActionLoading(false);
    }
  };

  // Redirect if not sudo
  if (authStore.initialized && !hasSudo) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          You do not have permission to access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/manager')}
        sx={{ mb: 2 }}
      >
        Back to Manager Tools
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          RQ Job Monitor
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchJobs} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label="Scheduled" value="scheduled" />
            <Tab label="Queued" value="queued" />
            <Tab label="Failed" value="failed" />
          </Tabs>
        </Box>

        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : jobs.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No {currentTab} jobs found.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Showing {jobs.length} of {total} jobs
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Job ID</TableCell>
                      <TableCell>Queue</TableCell>
                      <TableCell>Function</TableCell>
                      <TableCell>Args</TableCell>
                      {currentTab === 'scheduled' && <TableCell>Scheduled Time</TableCell>}
                      {currentTab === 'failed' && <TableCell>Error</TableCell>}
                      <TableCell>Status</TableCell>
                      <TableCell width={currentTab === 'failed' ? 120 : 60}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {jobs.map((job) => (
                      <TableRow key={job.job_id} hover>
                        <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Tooltip title={job.job_id}>
                            <span>{job.job_id.substring(0, 12)}...</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip label={job.queue || 'default'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={job.func_name}>
                            <span>{formatFuncName(job.func_name)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {formatArgs(job.args, job.kwargs)}
                          </Typography>
                        </TableCell>
                        {currentTab === 'scheduled' && (
                          <TableCell>{formatDate(job.scheduled_time)}</TableCell>
                        )}
                        {currentTab === 'failed' && (
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <Typography
                              variant="body2"
                              color="error"
                              sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                            >
                              {job.exc_info_truncated?.substring(0, 100) || '-'}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <Chip
                            label={job.status}
                            color={getStatusColor(job.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View details">
                            <IconButton size="small" onClick={() => handleViewJob(job.job_id)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {currentTab === 'failed' && (
                            <>
                              <Tooltip title="Requeue">
                                <IconButton size="small" onClick={() => handleRequeueJob(job.job_id)} color="primary">
                                  <ReplayIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => handleDeleteJob(job.job_id)} color="error">
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>

      <JobDetailDialog
        job={selectedJob}
        open={dialogOpen}
        onClose={handleDialogClose}
        loading={dialogLoading}
        onDelete={handleDeleteJob}
        onRequeue={handleRequeueJob}
        actionLoading={actionLoading}
      />
    </Container>
  );
});
